import { useMemo, useContext, useEffect, useCallback, FC } from 'react';
import { useSelector } from 'react-redux';
import { VirtualizedTable } from '@components/TableNew';
import { TableColumnsContext } from '@components/TableNew/TableColumns';
import TableSortingContext, { SortingOption } from '@components/TableNew/TableSorting';
import { getCopyTableData, formatCopyDataAsString } from '@components/TableNew/TableUtils';
import { GlobalContext } from '@components/App/App.context';
import PermissionOverlay from '@components/PermissionOverlay/PermissionOverlay';
import { useTopTrendsTableConfig } from './TopTrendsTableConfig';
import { Sort } from '@models';
import { useFilterSort } from '@core/hooks/useFilterSort';
import { TopTrendsDataItem, getTopTrendsCopyTableData } from '@core/apiHooks/useTopTrendsData';
import useRequestFlavorAccess from '@core/apiHooks/useRequestFlavorAccess';
import { currentCountrySelector } from '@selectors/countries.selector';
import { useTopTrends } from './TopTrendsContext';
import { toast } from "react-toastify";
import { useAiSearchSettingsContext } from "@vms/AiSearchSettingsContext/AiSearchSettingsContext";
import { TopTrendsSortingColumnsEnum } from "@models/TopTrends";
import { getSelectAllState } from './selectAll';

const sortingOptions: SortingOption[] = [
    { id: 'penetrationValue', name: 'penetrationValue', defaultDirection: Sort.Desc },
    { id: 'incidenceValue', name: 'incidenceValue', defaultDirection: Sort.Desc },
    { id: 'restaurantCount', name: 'restaurantCount', defaultDirection: Sort.Desc },
    { id: 'growthValue', name: 'growthValue', defaultDirection: Sort.Desc },
    { id: 'cagrValue', name: 'cagrValue', defaultDirection: Sort.Desc },
    { id: 'knowItValue', name: 'knowItValue', defaultDirection: Sort.Desc },
    { id: 'haveTriedItValue', name: 'haveTriedItValue', defaultDirection: Sort.Desc },
    { id: 'loveOrLikeItValue', name: 'loveOrLikeItValue', defaultDirection: Sort.Desc },
];

/** Maps an AI-search sorting column to the matching table data key. */
const getSortingKey = (sortingColumn: TopTrendsSortingColumnsEnum): keyof TopTrendsDataItem => {
    switch (sortingColumn) {
        case TopTrendsSortingColumnsEnum.Penetration:
            return 'penetrationValue';
        case TopTrendsSortingColumnsEnum.Incidence:
            return 'incidenceValue';
        case TopTrendsSortingColumnsEnum.PenetrationCT:
            return 'restaurantCount';
        case TopTrendsSortingColumnsEnum.PastGrowth:
            return 'growthValue';
        case TopTrendsSortingColumnsEnum.CAGR:
            return 'cagrValue';
        case TopTrendsSortingColumnsEnum.KnowIt:
            return 'knowItValue';
        case TopTrendsSortingColumnsEnum.HaveTriedIt:
            return 'haveTriedItValue';
        case TopTrendsSortingColumnsEnum.LoveOrLikeIt:
            return 'loveOrLikeItValue';
        default:
            return 'penetrationValue';
    }
}

/** Top Trends results table, including the Build keyword list selection controls. */
const TopTrendsTable: FC = () => {
    const { topTrendsSettings, resetSearch } = useAiSearchSettingsContext();
    const {
        setSortingDirection: setSortingDirectionUpper,
        setSortingKeyEnumValue,
    } = useTopTrends();
    const {
        tableData: data,
        flavorData,
        hasFlavorData,
        hasFlavorPermission,
        selectedWords,
        isSelectable,
        hasTags,
        growthPeriod,
        years,
        isLoading,
        onGrowthPeriodChange,
        onWordSelect,
        onSelectAll,
        onClearSelections,
        onSettingsClick,
        onDrillClick,
        onMenuExamplesClick,
        onCopyContentChange,
    } = useTopTrends();
    const { getRem } = useContext(GlobalContext);
    const currentCountry = useSelector(currentCountrySelector);
    const { requestAccess } = useRequestFlavorAccess();

    const showFlavorPermissionOverlay = useMemo(
        () => data.length > 0 && hasFlavorData && !hasFlavorPermission,
        [data.length, hasFlavorData, hasFlavorPermission]
    );

    const handleRequestAccessButtonClick = useCallback(() => {
        requestAccess(currentCountry, 'Top Trends');
        toast.success("Request sent");
    }, [currentCountry, requestAccess]);

    const {
        data: sortedData,
        sortingKey,
        sortingDirection,
        setSortingKey,
        setSortingDirection,
    } = useFilterSort<TopTrendsDataItem>(
        data,
        'penetrationValue',
        Sort.Desc,
    );

    useEffect(() => {
        setSortingDirectionUpper(sortingDirection);
    }, [sortingDirection, setSortingDirectionUpper]);

    useEffect(() => {
        let sortingKeyEnumValue: TopTrendsSortingColumnsEnum | null = null;
        switch (sortingKey) {
            case 'penetrationValue':
                sortingKeyEnumValue = TopTrendsSortingColumnsEnum.Penetration;
                break;
            case 'incidenceValue':
                sortingKeyEnumValue = TopTrendsSortingColumnsEnum.Incidence;
                break;
            case 'restaurantCount':
                sortingKeyEnumValue = TopTrendsSortingColumnsEnum.PenetrationCT;
                break;
            case 'growthValue':
                sortingKeyEnumValue = TopTrendsSortingColumnsEnum.PastGrowth;
                break;
            case 'cagrValue':
                sortingKeyEnumValue = TopTrendsSortingColumnsEnum.CAGR;
                break;
            case 'knowItValue':
                sortingKeyEnumValue = TopTrendsSortingColumnsEnum.KnowIt;
                break;
            case 'haveTriedItValue':
                sortingKeyEnumValue = TopTrendsSortingColumnsEnum.HaveTriedIt;
                break;
            case 'loveOrLikeItValue':
                sortingKeyEnumValue = TopTrendsSortingColumnsEnum.LoveOrLikeIt;
                break;
        }
        if (sortingKeyEnumValue !== null) {
            setSortingKeyEnumValue(sortingKeyEnumValue);
        }
    }, [sortingKey, setSortingKeyEnumValue]);

    /** Applies the sorting chosen by AI search settings to the table. */
    useEffect(() => {
        if (topTrendsSettings?.sortingColumn && topTrendsSettings?.sortingDirection) {
            setSortingKey(getSortingKey(topTrendsSettings.sortingColumn));
            setSortingDirection(topTrendsSettings.sortingDirection);
        }
    }, [
        topTrendsSettings?.sortingColumn,
        topTrendsSettings?.sortingDirection,
        setSortingKey,
        setSortingDirection,
    ]);

    /** Keyword names in the current filtered, sorted view, in display order. */
    const visibleWords = useMemo(
        () => sortedData.flatMap(item => (item.word?.name ? [item.word.name] : [])),
        [sortedData]
    );

    /** Header checkbox state derived from the visible words and the selection. */
    const selectAllState = useMemo(
        () => getSelectAllState(visibleWords, selectedWords),
        [visibleWords, selectedWords]
    );

    /** Clears every selection when checked; otherwise fills the selection from the visible rows. */
    const handleSelectAllClick = useCallback(() => {
        if (selectAllState === 'checked') {
            onClearSelections();
        } else {
            onSelectAll(visibleWords);
        }
    }, [selectAllState, visibleWords, onClearSelections, onSelectAll]);

    /** Column definitions for the sorted table data. */
    const tableConfig = useTopTrendsTableConfig({
        data: sortedData,
        flavorData,
        hasFlavorData,
        hasFlavorPermission,
        selectedWords,
        isSelectable,
        selectAllState,
        onSelectAllClick: handleSelectAllClick,
        hasTags,
        growthPeriod,
        years,
        onGrowthPeriodChange,
        onWordSelect,
        onSettingsClick,
        onDrillClick,
        onMenuExamplesClick,
    });

    const tableProviderValue = useMemo(
        () => ({ data: tableConfig }),
        [tableConfig]
    );

    const copyTableContent = useMemo(() => {
        if (sortedData.length === 0) return '';
        const baseData = getCopyTableData(tableConfig, sortedData.length);
        const copyData = getTopTrendsCopyTableData(baseData, growthPeriod);
        return formatCopyDataAsString(copyData);
    }, [tableConfig, sortedData.length, growthPeriod]);

    useEffect(() => {
        onCopyContentChange?.(copyTableContent);
    }, [copyTableContent, onCopyContentChange]);

    const sortContextValue = useMemo(
        () => ({
            value: {
                id: sortingKey as string,
                direction: sortingDirection,
            },
            options: sortingOptions,
            setValue: ({ id, direction }: { id: string | number; direction: Sort }) => {
                setSortingKey(id as keyof TopTrendsDataItem);
                setSortingDirection(direction);
                resetSearch();
            },
        }),
        [sortingKey, sortingDirection, setSortingKey, setSortingDirection, resetSearch]
    );

    const SKELETON_ROW_COUNT = 30;

    const displayRowCount = useMemo(() => {
        if (isLoading) {
            return SKELETON_ROW_COUNT;
        }
        return sortedData.length;
    }, [isLoading, sortedData.length]);

    return (
        <TableSortingContext.Provider value={sortContextValue}>
            <TableColumnsContext.Provider value={tableProviderValue}>
                <div className="TopTrendsTable">
                    <VirtualizedTable
                        isLoading={isLoading}
                        rowsCount={displayRowCount}
                        headHeight={getRem(55)}
                        rowHeight={getRem(50)}
                        showFallback
                        testId="topTrendsTable"
                    />
                    {showFlavorPermissionOverlay && (
                        <div className="TopTrendsTable__flavor-permission-overlay">
                            <PermissionOverlay
                                handleButtonClick={handleRequestAccessButtonClick}
                                variant={sortedData.length < 3 ? 'row narrow' : undefined}
                                hideIcon={sortedData.length <= 3}
                            />
                        </div>
                    )}
                </div>
            </TableColumnsContext.Provider>
        </TableSortingContext.Provider>
    );
};

export default TopTrendsTable;
